// A small country flag (Ben, 2026-10-16: the League chip's list shows each league's flag). The
// data names countries by ISO alpha-3 code ("ESP"; the home nations as "ENG", "SCO", "WAL", "NIR");
// the flag set (flag-icons, MIT) by alpha-2 ("es", "gb-eng"). No known code → no flag.

import type { StyledSelectGroup, StyledSelectItem } from "./StyledSelect.js";
import "flag-icons/css/flag-icons.min.css";
import type { OptionInfo } from "./FilterPopovers.js";
import { topLeagueLabels } from "../live/top-leagues.js";
import { leagueStrengthRank } from "../live/league-strength.js";

const ALPHA3_TO_FLAG: Record<string, string> = {
  AFG: "af",
  ALA: "ax",
  ALB: "al",
  DZA: "dz",
  ASM: "as",
  AND: "ad",
  AGO: "ao",
  AIA: "ai",
  ATA: "aq",
  ATG: "ag",
  ARG: "ar",
  ARM: "am",
  ABW: "aw",
  AUS: "au",
  AUT: "at",
  AZE: "az",
  BHS: "bs",
  BHR: "bh",
  BGD: "bd",
  BRB: "bb",
  BLR: "by",
  BEL: "be",
  BLZ: "bz",
  BEN: "bj",
  BMU: "bm",
  BTN: "bt",
  BOL: "bo",
  BES: "bq",
  BIH: "ba",
  BWA: "bw",
  BVT: "bv",
  BRA: "br",
  IOT: "io",
  BRN: "bn",
  BGR: "bg",
  BFA: "bf",
  BDI: "bi",
  CPV: "cv",
  KHM: "kh",
  CMR: "cm",
  CAN: "ca",
  CYM: "ky",
  CAF: "cf",
  TCD: "td",
  CHL: "cl",
  CHN: "cn",
  CXR: "cx",
  CCK: "cc",
  COL: "co",
  COM: "km",
  COG: "cg",
  COD: "cd",
  COK: "ck",
  CRI: "cr",
  CIV: "ci",
  HRV: "hr",
  CUB: "cu",
  CUW: "cw",
  CYP: "cy",
  CZE: "cz",
  DNK: "dk",
  DJI: "dj",
  DMA: "dm",
  DOM: "do",
  ECU: "ec",
  EGY: "eg",
  SLV: "sv",
  GNQ: "gq",
  ERI: "er",
  EST: "ee",
  SWZ: "sz",
  ETH: "et",
  FLK: "fk",
  FRO: "fo",
  FJI: "fj",
  FIN: "fi",
  FRA: "fr",
  GUF: "gf",
  PYF: "pf",
  ATF: "tf",
  GAB: "ga",
  GMB: "gm",
  GEO: "ge",
  DEU: "de",
  GHA: "gh",
  GIB: "gi",
  GRC: "gr",
  GRL: "gl",
  GRD: "gd",
  GLP: "gp",
  GUM: "gu",
  GTM: "gt",
  GGY: "gg",
  GIN: "gn",
  GNB: "gw",
  GUY: "gy",
  HTI: "ht",
  HMD: "hm",
  VAT: "va",
  HND: "hn",
  HKG: "hk",
  HUN: "hu",
  ISL: "is",
  IND: "in",
  IDN: "id",
  IRN: "ir",
  IRQ: "iq",
  IRL: "ie",
  IMN: "im",
  ISR: "il",
  ITA: "it",
  JAM: "jm",
  JPN: "jp",
  JEY: "je",
  JOR: "jo",
  KAZ: "kz",
  KEN: "ke",
  KIR: "ki",
  PRK: "kp",
  KOR: "kr",
  KWT: "kw",
  KGZ: "kg",
  LAO: "la",
  LVA: "lv",
  LBN: "lb",
  LSO: "ls",
  LBR: "lr",
  LBY: "ly",
  LIE: "li",
  LTU: "lt",
  LUX: "lu",
  MAC: "mo",
  MDG: "mg",
  MWI: "mw",
  MYS: "my",
  MDV: "mv",
  MLI: "ml",
  MLT: "mt",
  MHL: "mh",
  MTQ: "mq",
  MRT: "mr",
  MUS: "mu",
  MYT: "yt",
  MEX: "mx",
  FSM: "fm",
  MDA: "md",
  MCO: "mc",
  MNG: "mn",
  MNE: "me",
  MSR: "ms",
  MAR: "ma",
  MOZ: "mz",
  MMR: "mm",
  NAM: "na",
  NRU: "nr",
  NPL: "np",
  NLD: "nl",
  NCL: "nc",
  NZL: "nz",
  NIC: "ni",
  NER: "ne",
  NGA: "ng",
  NIU: "nu",
  NFK: "nf",
  MKD: "mk",
  MNP: "mp",
  NOR: "no",
  OMN: "om",
  PAK: "pk",
  PLW: "pw",
  PSE: "ps",
  PAN: "pa",
  PNG: "pg",
  PRY: "py",
  PER: "pe",
  PHL: "ph",
  PCN: "pn",
  POL: "pl",
  PRT: "pt",
  PRI: "pr",
  QAT: "qa",
  REU: "re",
  ROU: "ro",
  RUS: "ru",
  RWA: "rw",
  BLM: "bl",
  SHN: "sh",
  KNA: "kn",
  LCA: "lc",
  MAF: "mf",
  SPM: "pm",
  VCT: "vc",
  WSM: "ws",
  SMR: "sm",
  STP: "st",
  SAU: "sa",
  SEN: "sn",
  SRB: "rs",
  SYC: "sc",
  SLE: "sl",
  SGP: "sg",
  SXM: "sx",
  SVK: "sk",
  SVN: "si",
  SLB: "sb",
  SOM: "so",
  ZAF: "za",
  SGS: "gs",
  SSD: "ss",
  ESP: "es",
  LKA: "lk",
  SDN: "sd",
  SUR: "sr",
  SJM: "sj",
  SWE: "se",
  CHE: "ch",
  SYR: "sy",
  TWN: "tw",
  TJK: "tj",
  TZA: "tz",
  THA: "th",
  TLS: "tl",
  TGO: "tg",
  TKL: "tk",
  TON: "to",
  TTO: "tt",
  TUN: "tn",
  TUR: "tr",
  TKM: "tm",
  TCA: "tc",
  TUV: "tv",
  UGA: "ug",
  UKR: "ua",
  ARE: "ae",
  GBR: "gb",
  USA: "us",
  UMI: "um",
  URY: "uy",
  UZB: "uz",
  VUT: "vu",
  VEN: "ve",
  VNM: "vn",
  VGB: "vg",
  VIR: "vi",
  WLF: "wf",
  ESH: "eh",
  YEM: "ye",
  ZMB: "zm",
  ZWE: "zw",
  XKX: "xk",
  ENG: "gb-eng",
  SCO: "gb-sct",
  WAL: "gb-wls",
  NIR: "gb-nir",
};

/** The flag-icons code for an alpha-3 country code, or null. Pure. */
export function flagCodeOf(alpha3: string | null | undefined): string | null {
  return alpha3 ? (ALPHA3_TO_FLAG[alpha3.toUpperCase()] ?? null) : null;
}

export function CountryFlag({ code, title }: { code: string | null | undefined; title?: string }) {
  const flag = flagCodeOf(code);
  if (!flag) return null;
  return <span className={`fi fi-${flag} country-flag`} title={title} aria-hidden={title ? undefined : true} />;
}

/** A youth or reserve league, by its name ("U19 Ligaen", "Under 23 League", "Reserve League",
 *  "Primavera 1", "Liga II"). These go last in their country's list (Ben, 2026-10-18: the site has no
 *  league tier yet — asked of Prateek in the league-tiers proposal). Pure. */
export function isYouthOrReserve(name: string): boolean {
  return /\b(u-?\d{2}|under[- ]?\d{2}|youth|juniors?|reserves?|academy|primavera)\b|\bii$/i.test(name.replace(/\s*\([^)]*\)\s*$/, "").trim());
}

/** Goes last in its country (Ben, 2026-10-18: youth and reserve leagues): the DB's own youth flag
 *  (`competitionYouth`, #329) OR the name. Both, because each misses some (checked 2026-10-26 on
 *  all 276): the flag adds 19 the name can't tell (Premier League 2, Norway's "G14"–"G19", the
 *  NCAA leagues…) but misses "U18/19 National Championship"; and only names know reserve leagues
 *  ("Reserve League", "Liga II"). */
export function goesLast(l: { label: string; youth?: boolean | null }): boolean {
  return l.youth === true || isYouthOrReserve(l.label);
}

/** After every league on Ben's strength list (his ~140 entries). */
const UNLISTED = 10_000;

/** Where a league sorts within its country (lower first, then A→Z): Ben's strength order
 *  (live/league-strength.ts, #347 — Superliga before 1st Division), then the leagues it doesn't
 *  list, then youth / reserve leagues last (`goesLast`). Pure. */
export function leagueOrderInCountry(l: { label: string; countryName: string | null; womens: boolean | null; youth?: boolean | null }): number {
  if (goesLast(l)) return UNLISTED + 1;
  return leagueStrengthRank({ name: l.label, countryName: l.countryName, womens: l.womens }) ?? UNLISTED;
}

/** A league's name without the " (England)" the picker adds to tell shared names apart — for a
 *  list that already shows the flag under the country's heading (Ben, 2026-10-18). Pure. */
export function withoutCountry(label: string, countryName: string | null): string {
  const suffix = ` (${countryName ?? "International"})`;
  return label.endsWith(suffix) ? label.slice(0, -suffix.length) : label;
}

/**
 * A league dropdown's list (League Table, Match Results, Squad — Ben, 2026-10-18: "impossible to
 * find the right league"), in the League chip's order: Ben's top leagues first, then each country
 * A→Z with its leagues A→Z, "International" (no country) last. Each league has its flag.
 * These lists hold both genders: the shown league's gender comes first, in the top leagues and
 * within each country (Ben, 2026-10-26 — not men's and women's side by side by place, which put
 * Denmark's Kvindeliga before Superliga on a men's table).
 */
export function leagueSelectGroups(
  leagues: readonly { id: number; label: string; countryName?: string | null; countryCode?: string | null; womens?: boolean | null; youth?: boolean | null }[],
  /** The league shown now (its gender leads); null = none yet → men's first, as before. */
  shownId: number | null = null,
): StyledSelectGroup[] {
  const full = leagues.map((l) => ({ ...l, countryName: l.countryName ?? null, countryCode: l.countryCode ?? null, womens: l.womens ?? null }));
  const womensFirst = full.find((l) => l.id === shownId)?.womens === true;
  const otherGender = (l: (typeof full)[number]) => (l.womens === null || l.womens === womensFirst ? 0 : 1);
  // A stable sort keeps Ben's order within each gender.
  const top = topLeagueLabels(full).sort(
    (a, b) => otherGender(full.find((l) => l.label === a)!) - otherGender(full.find((l) => l.label === b)!),
  );
  const item = (l: (typeof full)[number]): StyledSelectItem => ({
    value: String(l.id),
    label: withoutCountry(l.label, l.countryName),
    buttonLabel: l.label,
    icon: <CountryFlag code={l.countryCode} />,
  });
  const groups = new Map<string, typeof full>();
  for (const l of full) {
    if (top.includes(l.label)) continue;
    const g = l.countryName ?? "International";
    groups.set(g, [...(groups.get(g) ?? []), l]);
  }
  const rank = (g: string) => (g === "International" ? 1 : 0);
  const out: StyledSelectGroup[] = [];
  if (top.length) {
    out.push({ group: "Top leagues", items: top.map((label) => item(full.find((l) => l.label === label)!)) });
  }
  for (const g of [...groups.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))) {
    // Youth / reserve last; before that the shown gender's leagues, then the other's; each in Ben's
    // strength order, then unlisted ones, A→Z within each.
    const order = (l: (typeof full)[number]) => leagueOrderInCountry(l);
    const last = (l: (typeof full)[number]) => (goesLast(l) ? 1 : 0);
    out.push({
      group: g,
      items: groups
        .get(g)!
        .sort((a, b) => last(a) - last(b) || otherGender(a) - otherGender(b) || order(a) - order(b) || a.label.localeCompare(b.label))
        .map(item),
    });
  }
  return out;
}

/** The League chip's list info: each league under its country, with its flag ("International"
 *  for a league with no country). Unknown label → none. */
export function leagueOptionInfo(
  leagues: readonly { label: string; countryName: string | null; countryCode: string | null; womens: boolean | null; youth?: boolean | null }[],
): (label: string) => OptionInfo | undefined {
  const byLabel = new Map(leagues.map((l) => [l.label, l] as const));
  // Ben's "Top leagues" (live/top-leagues.ts) come first, in his order, under their own heading.
  const top = topLeagueLabels(leagues);
  return (label) => {
    const l = byLabel.get(label);
    if (!l) return undefined;
    const icon = <CountryFlag code={l.countryCode} />;
    const text = withoutCountry(label, l.countryName);
    const t = top.indexOf(label);
    if (t !== -1) return { group: "Top leagues", groupRank: 0, rank: t, icon, text };
    // Within its country: Ben's strength order, then unlisted leagues, then youth / reserve.
    return { group: l.countryName ?? "International", icon, text, rank: leagueOrderInCountry(l) };
  };
}
